import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import { HandThumbUpIcon, PlayIcon } from '@heroicons/react/24/outline';
import type { YouTubeComment } from '@mermaids/shared';
import { API_ENDPOINTS } from '../../config/constants';
import { truncate, shuffleAndPick } from './utils';

const YouTubeCommentsSection = () => {
  const [comments, setComments] = useState<YouTubeComment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchComments();
  }, []);

  const fetchComments = async () => {
    try {
      const response = await fetch(
        `${API_ENDPOINTS.youtube}/comments?maxResults=20`
      );

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        setLoading(false);
        return;
      }

      const data = await response.json();
      if (data.success && data.data?.length > 0) {
        setComments(shuffleAndPick(data.data, 3));
      }
    } catch {
      /* comments are non-critical */
    } finally {
      setLoading(false);
    }
  };

  if (!loading && comments.length === 0) return null;

  return (
    <section className="py-20 lg:py-28 bg-[#faf6f1]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-14">
          <motion.span
            className="text-[#d4634a] text-[11px] font-semibold uppercase tracking-[0.25em]"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            Community
          </motion.span>
          <motion.h2
            className="font-editorial text-4xl md:text-5xl lg:text-6xl text-stone-900 mt-3 tracking-tight font-bold"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1, duration: 0.6 }}
          >
            Viewer{' '}
            <span className="italic font-light text-stone-300">voices</span>
          </motion.h2>
        </div>

        {/* Cards */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[0, 1, 2].map(i => (
              <div
                key={i}
                className="bg-white rounded-2xl border border-stone-100 p-7 animate-pulse"
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 bg-stone-100 rounded-full" />
                  <div className="flex-1">
                    <div className="h-3 bg-stone-100 rounded w-24 mb-2" />
                    <div className="h-2.5 bg-stone-50 rounded w-16" />
                  </div>
                </div>
                <div className="space-y-2.5">
                  <div className="h-3 bg-stone-100 rounded w-full" />
                  <div className="h-3 bg-stone-100 rounded w-5/6" />
                  <div className="h-3 bg-stone-50 rounded w-2/3" />
                </div>
                <div className="mt-6 pt-4 border-t border-stone-50">
                  <div className="h-2.5 bg-stone-50 rounded w-32" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {comments.map((comment, i) => (
              <motion.div
                key={comment.id}
                className="relative bg-white rounded-2xl border border-stone-100 p-7 hover:shadow-lg hover:shadow-stone-900/[0.04] transition-shadow duration-500"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{
                  delay: 0.15 * i,
                  duration: 0.6,
                  ease: 'easeOut',
                }}
              >
                {/* Decorative quote mark */}
                <span className="absolute top-5 right-6 font-editorial text-6xl text-stone-100 leading-none select-none pointer-events-none">
                  "
                </span>

                {/* Author row */}
                <div className="flex items-center gap-3 mb-5 relative z-10">
                  <img
                    src={comment.authorProfileImageUrl}
                    alt={comment.authorDisplayName}
                    className="w-10 h-10 rounded-full object-cover ring-2 ring-stone-100"
                    referrerPolicy="no-referrer"
                  />
                  <div>
                    <p className="text-sm font-semibold text-stone-800 leading-tight">
                      {comment.authorDisplayName}
                    </p>
                    <p className="text-[11px] text-stone-400 mt-0.5">
                      {formatDistanceToNow(new Date(comment.publishedAt), {
                        addSuffix: true,
                      })}
                    </p>
                  </div>
                </div>

                {/* Comment text */}
                <p className="text-stone-600 text-sm leading-relaxed relative z-10">
                  {truncate(comment.textDisplay, 180)}
                </p>

                {/* Footer */}
                <div className="mt-6 pt-4 border-t border-stone-100 flex items-center justify-between relative z-10">
                  {comment.videoTitle && (
                    <a
                      href={`https://www.youtube.com/watch?v=${comment.videoId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-[11px] text-stone-400 hover:text-[#d4634a] transition-colors max-w-[70%] truncate"
                    >
                      <PlayIcon className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="truncate">{comment.videoTitle}</span>
                    </a>
                  )}
                  {comment.likeCount > 0 && (
                    <span className="flex items-center gap-1 text-[11px] text-stone-300 flex-shrink-0">
                      <HandThumbUpIcon className="w-3.5 h-3.5" />
                      {comment.likeCount}
                    </span>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default YouTubeCommentsSection;
